import { Avatar } from "@mui/material";
import * as User from "providers/user";
import {
  FunctionField,
  List,
  useCreate,
  useDelete,
  useRefresh,
  useTranslate
} from "react-admin";
import CustomDataGrid from "./CustomDataGrid";
import { Link, useParams } from "react-router-dom";
import { CustomButton } from "./CustomButton";
import { muiColors } from "../themes/colors";

export function ArtistExclusions() {
  const { id } = useParams<{ id: string }>();
  const { permissions } = User.useUser();
  const [create] = useCreate();
  const [deleteOne] = useDelete();
  const translate = useTranslate();
  const refresh = useRefresh();

  const filter = {
    role: {
      is: {
        role: {
          in: ["SUPER", "OWNER", "ARTIST", "MANAGER"]
        },
        company_id: {
          equals: permissions.companyId
        }
      }
    }
  };

  const handleExclusion = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    record: any,
    isExcluded: boolean
  ) => {
    e.stopPropagation();
    try {
      if (isExcluded) {
        await deleteOne(
          "ArtistServiceExclusion",
          {
            id: record?.artist?.service_exclusions?.find(
              (exclusion: any) => exclusion.service_id === id
            )?.id
          },
          {
            onSuccess: () => refresh()
          }
        );
      } else {
        await create(
          "ArtistServiceExclusion",
          {
            data: {
              artist: { id: record?.artist.id },
              service: { id }
            }
          },
          {
            onSuccess: () => refresh()
          }
        );
      }
    } catch (error) {
      throw new Error(error);
    }
  };

  return (
    <List
      filter={filter}
      resource="User"
      title={translate("pagenames.artist_exclusions")}
      actions={<div className="h-0" />}
      sx={{
        marginTop: "4px",
        "& .RaList-main > div": {
          minHeight: "0px"
        }
      }}
      hasCreate={false}
      exporter={false}
    >
      <CustomDataGrid
        bulkActionButtons={false}
        rowClick={false}
        sx={{
          borderRadius: "0px",
          ".RaDatagrid-headerCell": {
            backgroundColor: "transparent",
            fontSize: "14px",
            "@media screen and (max-width:1280px)": {
              fontSize: "12px"
            }
          },
          ".RaDatagrid-rowCell": {
            borderBottom: "solid 1px rgba(16, 39, 52, 0.05)",
            padding: "6px 2px"
          },
          "td:last-child": {
            width: "114px"
          },
          ".RaDatagrid-rowCell span": {
            fontSize: "14px",
            "@media screen and (max-width:1280px)": {
              fontSize: "12px"
            }
          }
        }}
      >
        <FunctionField
          label={translate("artists.technicians")}
          render={(record: any) => (
            <div
              className="max-w-fit"
              onClick={(e) => e.stopPropagation()}
              aria-hidden
            >
              <Link
                to={`/dashboard/Customer/${
                  record?.user?.id || record?.id
                }/show`}
                className="flex items-center gap-[16px]"
              >
                <Avatar
                  sx={{
                    bgcolor: "rgba(238, 234, 228, 0.9)",
                    color: "rgba(48, 47, 46, 0.95)",
                    width: "32px",
                    height: "32px",
                    fontFamily: "Inter",
                    fontSize: "18px"
                  }}
                  alt={record?.first_name}
                  src={
                    record?.artist?.cover_photo_url ||
                    record?.photo_url ||
                    "monogram"
                  }
                />
                <div
                  className="line-clamp-1 break-all text-[#16667A] 
                font-['Inter'] leading-[1.43] text-[14px]"
                >
                  {`${record?.first_name} ${record?.last_name}`}
                </div>
              </Link>
            </div>
          )}
        />
        <FunctionField
          label={
            <span className="mr-[16px]">{translate("artists.exclude")}</span>
          }
          textAlign="right"
          sx={{ display: "block", width: "114px" }}
          render={(record: any) => {
            const isExcluded = record?.artist?.service_exclusions?.some(
              (exclusion: any) => exclusion.service_id === id
            );
            return (
              <div className="w-max-[114px] w-full flex justify-end">
                <CustomButton
                  text={
                    isExcluded
                      ? translate("services.enable")
                      : translate("services.disable")
                  }
                  variant="secondary"
                  onClick={(
                    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
                  ) => handleExclusion(e, record, isExcluded)}
                  sx={{
                    color: isExcluded ? muiColors.text : muiColors.error
                  }}
                />
              </div>
            );
          }}
        />
      </CustomDataGrid>
    </List>
  );
}
